import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  limit,
  orderBy,
  startAt,
  endAt,
  documentId,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, Class, AlarmSet, DeletedClass, ClassMember, AssignmentSettings } from '../types/alarm';
import { firebaseReadCounter } from './firebaseReadCounter';
import { classCodeService } from './classCodeService';
import { defaultSetService } from './defaultSetService';
import { withMemberGroup } from '../utils/memberGroupPaths';
import { memberProfilePhotoService } from './memberProfilePhotoService';

// Firestore 컬렉션 이름들
const COLLECTIONS = {
  USERS: 'users',
  CLASSES: 'classes',
  ALARM_SETS: 'alarmSets',
  DELETED_CLASSES: 'deletedClasses',
  USED_CLASS_CODES: 'usedClassCodes'
};

export const userDataService = {
  // ====== 멤버 캐시 (그룹별) ======
  // - 읽기 비용 절감용: 같은 userId/classId/memberGroup의 members는 일정 시간 캐시
  // - 변경(저장/삭제/프로필변경) 시 즉시 무효화
  __classMembersCache: new Map<string, { ts: number; members: ClassMember[] }>(),
  __classMembersCacheTTL: 5 * 60 * 1000, // 5분

  __getMembersCacheKey(userId: string, classId: string, memberGroup?: string) {
    const g = (memberGroup || 'A').toString().trim().toUpperCase();
    return `members_cache_${userId}_${classId}_${g}`;
  },

  __getMembersMemoryKey(userId: string, classId: string, memberGroup?: string) {
    const g = (memberGroup || 'A').toString().trim().toUpperCase();
    return `${userId}:${classId}:${g}`;
  },

  __readMembersCache(userId: string, classId: string, memberGroup?: string): ClassMember[] | null {
    const memoryKey = this.__getMembersMemoryKey(userId, classId, memberGroup);
    const now = Date.now();
    const mem = this.__classMembersCache.get(memoryKey);
    if (mem && now - mem.ts < this.__classMembersCacheTTL) {
      return mem.members;
    }

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const key = this.__getMembersCacheKey(userId, classId, memberGroup);
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { ts: number; members: ClassMember[] };
        if (!parsed?.ts || !Array.isArray(parsed.members)) return null;
        if (now - parsed.ts > this.__classMembersCacheTTL) return null;
        this.__classMembersCache.set(memoryKey, { ts: parsed.ts, members: parsed.members });
        return parsed.members;
      }
    } catch {
      // ignore
    }
    return null;
  },

  __writeMembersCache(userId: string, classId: string, memberGroup: string | undefined, members: ClassMember[]) {
    const memoryKey = this.__getMembersMemoryKey(userId, classId, memberGroup);
    const payload = { ts: Date.now(), members };
    this.__classMembersCache.set(memoryKey, payload);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const key = this.__getMembersCacheKey(userId, classId, memberGroup);
        window.localStorage.setItem(key, JSON.stringify(payload));
      }
    } catch {
      // ignore
    }
  },

  // 캐시 무효화 (외부에서 호출 가능하도록 public 메소드 추가)
  invalidateMembersCache(userId: string, classId: string, memberGroup?: string) {
    this.__invalidateMembersCache(userId, classId, memberGroup);
  },

  __invalidateMembersCache(userId: string, classId: string, memberGroup?: string) {
    const memoryKey = this.__getMembersMemoryKey(userId, classId, memberGroup);
    this.__classMembersCache.delete(memoryKey);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const key = this.__getMembersCacheKey(userId, classId, memberGroup);
        window.localStorage.removeItem(key);
      }
    } catch {
      // ignore
    }
  },

  // classMemberships: 특정 멤버의 문서 삭제 (문서 ID = 익명 uid, memberId 필드로 검색)
  async __deleteClassMembershipForMember(userId: string, classId: string, memberId: string): Promise<void> {
    try {
      const membersRef = collection(db, COLLECTIONS.USERS, userId, 'classMemberships', classId, 'members');
      const membershipQuery = query(membersRef, where('memberId', '==', memberId), limit(1));
      const snapshot = await getDocs(membershipQuery);
      if (!snapshot.empty) {
        await deleteDoc(snapshot.docs[0].ref);
        console.log('🗑️ classMembership 삭제:', { classId, memberId });
      }
    } catch (e) {
      console.warn('classMembership 삭제 실패 (무시):', e);
    }
  },

  // classMemberships: 클래스의 모든 멤버 문서 삭제
  async __deleteClassMembershipsForClass(userId: string, classId: string): Promise<void> {
    try {
      const membersRef = collection(db, COLLECTIONS.USERS, userId, 'classMemberships', classId, 'members');
      const snapshot = await getDocs(membersRef);
      for (const docSnap of snapshot.docs) {
        await deleteDoc(docSnap.ref);
      }
      if (snapshot.docs.length > 0) {
        console.log('🗑️ classMemberships 삭제:', { classId, count: snapshot.docs.length });
      }
    } catch (e) {
      console.warn('classMemberships 삭제 실패 (무시):', e);
    }
  },

  // 사용자 프로필 저장
  async saveUserProfile(user: User): Promise<void> {
    try {
      console.log('💾 saveUserProfile - 사용자 프로필 저장 시작:', {
        userId: user.id,
        currentClassId: user.currentClassId
      });
      
      const userRef = doc(db, COLLECTIONS.USERS, user.id);
      
      await setDoc(userRef, {
        id: user.id,
        email: user.email,
        name: user.name,
        picture: user.picture,
        userType: user.userType,
        currentClassId: user.currentClassId,
        ...(user.termsAgreed !== undefined && { termsAgreed: user.termsAgreed }),
        ...(user.termsAgreedAt && { termsAgreedAt: user.termsAgreedAt }),
        updatedAt: new Date()
      }, { merge: true });
      
      console.log('✅ saveUserProfile - 사용자 프로필 저장 완료:', {
        userId: user.id
      });
    } catch (error) {
      console.error('사용자 프로필 저장 실패:', error);
      throw error;
    }
  },

  // 사용자 프로필 로드
  async loadUserProfile(userId: string): Promise<User | null> {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        console.log('🔍 loadUserProfile - Firebase에서 로드된 원본 userData:', {
          currentClassId: userData.currentClassId
        });
        
        return {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          userType: userData.userType,
          currentClassId: userData.currentClassId,
          termsAgreed: userData.termsAgreed,
          termsAgreedAt: userData.termsAgreedAt
        } as User;
      }
      
      console.log('⚠️ loadUserProfile - 사용자 문서가 존재하지 않음:', userId);
      return null;
    } catch (error) {
      console.error('사용자 프로필 로드 실패:', error);
      throw error;
    }
  },

  // 개별 클래스 저장 (컬렉션)
  async saveClass(userId: string, classData: Class): Promise<void> {
    try {
      console.log('💾 saveClass - 개별 클래스 저장 시작:', {
        userId,
        classId: classData.id,
        className: classData.name
      });
      
      const classRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classData.id);
      
      // 기존 클래스 데이터 읽기 (클래스 코드 변경 확인용)
      const existingClassDoc = await getDoc(classRef);
      const existingClassData = existingClassDoc.exists() ? existingClassDoc.data() as Class : null;
      
      // 클래스 코드가 변경되었으면 이전 코드 해제
      if (existingClassData?.classCode && 
          classData.classCode && 
          existingClassData.classCode !== classData.classCode) {
        console.log('🔄 클래스 코드 변경 감지:', {
          이전코드: existingClassData.classCode,
          새코드: classData.classCode
        });
        await classCodeService.unregisterClassCode(existingClassData.classCode);
        console.log('✅ 이전 클래스 코드 해제 완료');
      }
      
      // undefined 값들을 null로 변환하거나 제거
      const cleanClassData = Object.fromEntries(
        Object.entries(classData).filter(([_, value]) => value !== undefined)
      );
      
      const classToSave = {
        ...cleanClassData,
        createdAt: classData.createdAt instanceof Date ? classData.createdAt.toISOString() : classData.createdAt,
        updatedAt: new Date().toISOString()
      };
      
      console.log('💾 클래스 저장:', {
        classId: classData.id,
        className: classData.name,
        classCode: classData.classCode,
        saved: (classToSave as any).classCode
      });
      
      await setDoc(classRef, classToSave);
      
      // 새로운 클래스 코드가 있으면 usedClassCodes에 등록
      if (classData.classCode) {
        await classCodeService.registerClassCode(
          classData.classCode,
          userId,
          classData.id,
          classData.name
        );
        console.log('✅ 새 클래스 코드 등록 완료:', classData.classCode);
      }
      
      console.log('✅ saveClass - 개별 클래스 저장 완료:', {
        userId,
        classId: classData.id
      });
    } catch (error) {
      console.error('개별 클래스 저장 실패:', error);
      throw error;
    }
  },

  // 개별 클래스 로드
  async loadClass(userId: string, classId: string): Promise<Class | null> {
    try {
      const classRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId);
      const classSnap = await getDoc(classRef);
      
      if (classSnap.exists()) {
        const data = classSnap.data();
        return {
          ...data,
          id: classSnap.id,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
          updatedAt: data.updatedAt ? new Date(data.updatedAt) : undefined
        } as Class;
      }
      
      return null;
    } catch (error) {
      console.error('개별 클래스 로드 실패:', error);
      return null;
    }
  },

  // 클래스 삭제 (컬렉션에서)
  async deleteClass(userId: string, classId: string): Promise<void> {
    try {
      console.log('🗑️ deleteClass - 클래스 삭제 시작:', { userId, classId });
      
      // 클래스 데이터 먼저 읽어서 classCode 확인
      const classRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId);
      const classDoc = await getDoc(classRef);
      
      if (classDoc.exists()) {
        const classData = classDoc.data() as Class;
        
        // 클래스 코드가 있으면 usedClassCodes에서 해제
        if (classData.classCode) {
          console.log('🔓 클래스 코드 해제:', classData.classCode);
          await classCodeService.unregisterClassCode(classData.classCode);
        }
      }
      
      // 클래스 문서 삭제
      await deleteDoc(classRef);
      
      console.log('✅ deleteClass - 클래스 삭제 완료:', { userId, classId });
    } catch (error) {
      console.error('클래스 삭제 실패:', error);
      throw error;
    }
  },

  // 사용자 클래스 목록 로드 (컬렉션에서)
  async loadUserClasses(userId: string): Promise<Class[]> {
    try {
      console.log('🔍 loadUserClasses - 클래스 목록 로드 시작:', { userId });
      
      const classesSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES));
      const classes: Class[] = [];
      
      classesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log('🔍 클래스 데이터 로드:', { 
          classId: doc.id, 
          className: data.name,
          coinSettings: data.coinSettings 
        });
        classes.push({
          ...data,
          id: doc.id,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
          updatedAt: data.updatedAt ? new Date(data.updatedAt) : undefined
        } as Class);
      });
      
      console.log('✅ loadUserClasses - 클래스 목록 로드 완료:', {
        userId,
        classesCount: classes.length
      });
      
      return classes.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    } catch (error) {
      console.error('클래스 목록 로드 실패:', error);
      return [];
    }
  },

  // 현재 클래스 ID 저장
  async saveCurrentClassId(userId: string, classId: string): Promise<void> {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        currentClassId: classId,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('현재 클래스 ID 저장 실패:', error);
      throw error;
    }
  },

  // 현재 클래스 ID 로드
  async loadCurrentClassId(userId: string): Promise<string | null> {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        return userData.currentClassId || null;
      }
      return null;
    } catch (error) {
      console.error('현재 클래스 ID 로드 실패:', error);
      throw error;
    }
  },

  // 사용자 알람세트 저장 (더 이상 사용하지 않음 - 클래스별 저장으로 대체됨)
  // async saveUserAlarmSets(userId: string, alarmSets: AlarmSet[]): Promise<void> {
  //   console.warn('⚠️ saveUserAlarmSets는 더 이상 사용되지 않습니다. saveClassAlarmSets를 사용하세요.');
  // },

  // 사용자 알람세트 로드 (더 이상 사용하지 않음 - 클래스별 로드로 대체됨)
  // async loadUserAlarmSets(userId: string): Promise<AlarmSet[]> {
  //   console.warn('⚠️ loadUserAlarmSets는 더 이상 사용되지 않습니다. loadClassAlarmSets를 사용하세요.');
  //   return [];
  // },

  // 클래스별 알람세트 저장
  async saveClassAlarmSets(userId: string, classId: string, alarmSets: AlarmSet[]): Promise<void> {
    try {
      console.log('💾 saveClassAlarmSets 호출:', { userId, classId, alarmSetsCount: alarmSets.length });
      const batch = writeBatch(db);
      
      // 기존 클래스별 알람세트 삭제
      const existingQuery = query(
        collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, COLLECTIONS.ALARM_SETS)
      );
      const existingSnapshot = await getDocs(existingQuery);
      console.log('🗑️ 삭제할 기존 알람세트 수:', existingSnapshot.docs.length);
      existingSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // 새로운 클래스별 알람세트 저장
      alarmSets.forEach(alarmSet => {
        const alarmSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, COLLECTIONS.ALARM_SETS, alarmSet.id);
        batch.set(alarmSetRef, {
          ...alarmSet,
          updatedAt: new Date()
        });
      });
      
      await batch.commit();
      console.log('✅ 알람세트 저장 완료:', { userId, classId, savedCount: alarmSets.length });
    } catch (error) {
      console.error('❌ 클래스별 알람세트 저장 실패:', error);
      throw error;
    }
  },

  // 클래스별 알람세트 로드
  async loadClassAlarmSets(userId: string, classId: string): Promise<AlarmSet[]> {
    try {
      console.log('🔍 loadClassAlarmSets 호출:', { userId, classId });
      const q = query(
        collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, COLLECTIONS.ALARM_SETS)
      );
      const querySnapshot = await getDocs(q);
      const alarmSets = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AlarmSet[];
      console.log('📦 Firestore에서 로드된 알람세트:', alarmSets);
      return alarmSets;
    } catch (error) {
      console.error('❌ 클래스별 알람세트 로드 실패:', error);
      throw error;
    }
  },

  // 삭제된 클래스 저장
  async saveDeletedClass(userId: string, deletedClass: DeletedClass): Promise<void> {
    try {
      const deletedClassRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, deletedClass.id);
      await setDoc(deletedClassRef, {
        ...deletedClass,
        createdAt:
          Object.prototype.toString.call(deletedClass.createdAt) === '[object Date]'
            ? (deletedClass.createdAt as unknown as Date).toISOString()
            : deletedClass.createdAt,
        willBeDeletedAt:
          Object.prototype.toString.call(deletedClass.willBeDeletedAt) === '[object Date]'
            ? (deletedClass.willBeDeletedAt as unknown as Date).toISOString()
            : deletedClass.willBeDeletedAt,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('삭제된 클래스 저장 실패:', error);
      throw error;
    }
  },

  // 삭제된 클래스 목록 로드
  async loadDeletedClasses(userId: string): Promise<DeletedClass[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES)
      );
      const querySnapshot = await getDocs(q);
      const deletedClasses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DeletedClass[];
      
      // 14일이 지나지 않은 것만 반환하고, 지난 것은 삭제
      const now = Date.now();
      const validDeletedClasses = deletedClasses.filter(deletedClass => {
        const willBeDeletedAt = new Date(deletedClass.willBeDeletedAt).getTime();
        if (willBeDeletedAt <= now) {
          // 만료된 것은 삭제
          this.deleteDeletedClass(userId, deletedClass.id);
          return false;
        }
        return true;
      });
      
      return validDeletedClasses.map(deletedClass => ({
        ...deletedClass,
        createdAt: new Date(deletedClass.createdAt),
        willBeDeletedAt: new Date(deletedClass.willBeDeletedAt).getTime()
      })) as DeletedClass[];
    } catch (error) {
      console.error('삭제된 클래스 로드 실패:', error);
      throw error;
    }
  },

  // 삭제된 클래스 삭제
  async deleteDeletedClass(userId: string, classId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId));
    } catch (error) {
      console.error('삭제된 클래스 삭제 실패:', error);
      throw error;
    }
  },

  // 사용자 데이터 전체 로드 (프로필 + 클래스 + 알람세트)
  async loadUserData(userId: string): Promise<{
    user: User | null;
    classes: Class[];
    alarmSets: AlarmSet[];
    currentClassId: string | null;
  }> {
    try {
      // 사용자 프로필 로드 (classes 포함)
      const user = await this.loadUserProfile(userId);
      const currentClassId = await this.loadCurrentClassId(userId);

      // 현재 클래스의 알람 세트 로드
      let alarmSets: AlarmSet[] = [];
      if (currentClassId) {
        try {
          alarmSets = await this.loadClassAlarmSets(userId, currentClassId);
        } catch (error) {
          console.error('현재 클래스 알람세트 로드 실패:', error);
        }
      }

      return {
        user,
        classes: [], // 컬렉션 기반으로 변경됨
        alarmSets,
        currentClassId
      };
    } catch (error) {
      console.error('사용자 데이터 전체 로드 실패:', error);
      throw error;
    }
  },

  // 사용자 데이터 전체 저장 (알람셋은 클래스별로 별도 저장)
  async saveUserData(user: User, alarmSets: AlarmSet[] = []): Promise<void> {
    try {
      console.log('💾 saveUserData - 사용자 데이터 전체 저장 시작:', {
        userId: user.id,
        currentClassId: user.currentClassId,
        hasCurrentClassId: !!user.currentClassId
      });
      
      // saveUserProfile에서 이미 classes를 저장하므로 saveUserClasses는 제거
      await Promise.all([
        this.saveUserProfile(user),  // ✅ classes 포함하여 저장
        // this.saveUserClasses(user.id, user.classes),  // ❌ 중복 제거
        this.saveCurrentClassId(user.id, user.currentClassId || '')
      ]);
      
      console.log('✅ saveUserData - 사용자 데이터 전체 저장 완료');
    } catch (error) {
      console.error('사용자 데이터 전체 저장 실패:', error);
      throw error;
    }
  },

  // 사용자 데이터 삭제 (로그아웃 시)
  async deleteUserData(userId: string): Promise<void> {
    try {
      // 사용자 문서 삭제 (하위 컬렉션들도 함께 삭제됨)
      await deleteDoc(doc(db, COLLECTIONS.USERS, userId));
    } catch (error) {
      console.error('사용자 데이터 삭제 실패:', error);
      throw error;
    }
  },

  // 사용자 계정 및 모든 하위 컬렉션 삭제
  async deleteUserAccount(userId: string): Promise<void> {
    const deleteCollection = async (collectionPath: string) => {
      const snapshot = await getDocs(collection(db, collectionPath));
      const deletions = snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));
      await Promise.all(deletions);
    };

    const deleteRoutineAssignmentDateAssignments = async (classId: string) => {
      const routineStatusesSnap = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'routineAssignmentStatuses'));
      for (const routineDoc of routineStatusesSnap.docs) {
        const dateAssignPath = `users/${userId}/classes/${classId}/routineAssignmentStatuses/${routineDoc.id}/DateAssignment`;
        await deleteCollection(dateAssignPath);
      }
    };

    const deleteClassBoardNotes = async (classId: string) => {
      // NoteBook/notes 하위 제거 후 NoteBook 문서 제거
      const notesPath = `users/${userId}/classes/${classId}/ClassBoard/NoteBook/notes`;
      await deleteCollection(notesPath);
      await deleteDoc(doc(db, `users/${userId}/classes/${classId}/ClassBoard`, 'NoteBook'));
      // ClassBoard 내 다른 문서가 있다면 일괄 삭제
      await deleteCollection(`users/${userId}/classes/${classId}/ClassBoard`);
    };

    try {
      // 클래스 하위 컬렉션부터 정리
      const classesSnap = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES));
      for (const classDoc of classesSnap.docs) {
        const classId = classDoc.id;
        const classData = classDoc.data();
        const classCode = (classData as any)?.classCode;
        const classBasePath = `users/${userId}/classes/${classId}`;
        const classSubCollections = [
          'ClassMembersProfile',
          'ClassMembersNote',
          'ClassMembersPartition',
          'ClassMembersNotePartition',
          'ClassMembersNotes',
          'ClassMembersNotesPartition',
          'alarmSets',
          'setCollection',
          'routineAssignmentStatuses',
          'routineAssignments',
          'assignmentStatuses',
          'assignments',
          'timeTags',
          'timeTagCategories',
          'ClassMembersCoin',
          'ClassMembersPoint',
          'ClassBoard'
        ];

        for (const sub of classSubCollections) {
          await deleteCollection(`${classBasePath}/${sub}`);
        }

        // 중첩 하위 컬렉션/문서 정리
        await deleteRoutineAssignmentDateAssignments(classId);
        await deleteClassBoardNotes(classId);

        await deleteDoc(classDoc.ref);

        // usedClassCodes에서 해당 클래스 코드 제거 (classCode가 존재할 때만)
        if (classCode) {
          try {
            await deleteDoc(doc(db, COLLECTIONS.USED_CLASS_CODES, classCode));
          } catch (error) {
            console.warn('⚠️ usedClassCodes 삭제 실패 (무시):', { classCode, error });
          }
        }
      }

      // 사용자 최상위 하위 컬렉션 정리
      await deleteCollection(`users/${userId}/${COLLECTIONS.CLASSES}`);
      await deleteCollection(`users/${userId}/${COLLECTIONS.ALARM_SETS}`);
      await deleteCollection(`users/${userId}/${COLLECTIONS.DELETED_CLASSES}`);
      await deleteCollection(`users/${userId}/${COLLECTIONS.USED_CLASS_CODES}`);

      // classMemberships 정리 (삭제한 클래스들에 대해)
      for (const classDoc of classesSnap.docs) {
        await this.__deleteClassMembershipsForClass(userId, classDoc.id);
      }

      // 전역 usedClassCodes 컬렉션에서 userId 매핑 제거 (안전망)
      const usedCodesSnapshot = await getDocs(
        query(collection(db, COLLECTIONS.USED_CLASS_CODES), where('userId', '==', userId))
      );
      if (!usedCodesSnapshot.empty) {
        const deletions = usedCodesSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));
        await Promise.all(deletions);
      }

      // 최종 사용자 문서 삭제
      await deleteDoc(doc(db, COLLECTIONS.USERS, userId));
    } catch (error) {
      console.error('사용자 계정 전체 삭제 실패:', error);
      throw error;
    }
  },

  // === 클래스 멤버 관리 함수들 ===
  
  // 클래스 멤버 저장 (새로운 구조: ClassMembers 문서의 배열에 저장)
  async saveClassMember(userId: string, classId: string, member: ClassMember): Promise<void> {
    try {
      console.log('💾 saveClassMember - 클래스 멤버 저장 시작:', {
        userId, classId, memberId: member.id, memberName: member.name
      });
      
      const classMembersRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'ClassMembers', classId);
      
      // 1. 기존 ClassMembers 문서 읽기
      const snapshot = await getDoc(classMembersRef);
      const existingMembers = snapshot.exists() ? (snapshot.data().members as ClassMember[]) || [] : [];
      
      // 2. 기존 멤버 업데이트 또는 새 멤버 추가 (코인은 ClassMembersCoin에서 관리하므로 저장하지 않음)
      const memberIndex = existingMembers.findIndex(m => m.id === member.id);
      const { coins, coinsHistory, ...memberRest } = member;
      const updatedMember = {
        ...memberRest,
        createdAt: member.createdAt instanceof Date ? member.createdAt : new Date(member.createdAt),
        updatedAt: new Date()
      };
      
      let updatedMembers: ClassMember[];
      if (memberIndex >= 0) {
        // 기존 멤버 업데이트
        updatedMembers = [...existingMembers];
        updatedMembers[memberIndex] = updatedMember;
      } else {
        // 새 멤버 추가
        updatedMembers = [...existingMembers, updatedMember];
      }
      
      // 3. ClassMembers 문서 업데이트
      await setDoc(classMembersRef, {
        members: updatedMembers,
        updatedAt: new Date()
      }, { merge: true });

      const { boardService } = await import('./boardService');
      await boardService.ensureNoticeNotesMemberDoc(userId, classId, member.id);
      
      console.log('✅ saveClassMember - 클래스 멤버 저장 완료');
    } catch (error) {
      console.error('클래스 멤버 저장 실패:', error);
      throw error;
    }
  },

  // 클래스 멤버 목록 로드 (파티션 서비스 사용)
  async loadClassMembers(userId: string, classId: string, memberGroup?: string): Promise<ClassMember[]> {
    try {
      console.log('🔍 loadClassMembers 시작:', { userId, classId, memberGroup });

      const cached = this.__readMembersCache(userId, classId, memberGroup);
      if (cached) {
        console.log('💾 loadClassMembers 캐시 사용:', { count: cached.length });
        return cached.sort((a, b) => parseInt(a.number) - parseInt(b.number));
      }
      
      // ClassMembersPartitionService 사용
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      
      const members = await partitionService.loadAllMembers();
      firebaseReadCounter.increment(1); // 파티션 수만큼 읽기 발생하지만 통합 카운트
      
      console.log('✅ loadClassMembers 완료:', { membersCount: members.length });
      const sorted = members.sort((a, b) => parseInt(a.number) - parseInt(b.number)); // 번호순 정렬
      this.__writeMembersCache(userId, classId, memberGroup, sorted);
      return sorted;
    } catch (error) {
      console.error('클래스 멤버 로드 실패:', error);
      throw error;
    }
  },

  // 클래스 멤버 삭제 (파티션 서비스 사용)
  async deleteClassMember(userId: string, classId: string, memberId: string, memberGroup?: string): Promise<void> {
    try {
      console.log('🗑️ deleteClassMember 시작:', { userId, classId, memberId, memberGroup });
      
      // ClassMembersPartitionService 사용
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      
      await partitionService.deleteMember(memberId);
      this.__invalidateMembersCache(userId, classId, memberGroup);

      // classMemberships 문서 삭제 (개별 접근 코드로 입장한 멤버 기록)
      await this.__deleteClassMembershipForMember(userId, classId, memberId);

      const { boardService } = await import('./boardService');
      await boardService.deleteNoticeNotesMemberDoc(userId, classId, memberId);
      await memberProfilePhotoService.deleteMemberPhoto(userId, classId, memberId);
      
      console.log('✅ deleteClassMember 완료');
    } catch (error) {
      console.error('클래스 멤버 삭제 실패:', error);
      throw error;
    }
  },

  // 클래스 멤버 일괄 저장 (파티션 서비스 사용)
  async saveClassMembersBatch(userId: string, classId: string, members: ClassMember[], memberGroup?: string): Promise<void> {
    try {
      console.log('💾 saveClassMembersBatch - 일괄 저장 시작:', {
        userId, classId, membersCount: members.length, memberGroup
      });
      
      // ClassMembersPartitionService 사용
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      
      await partitionService.saveMembers(members);
      this.__invalidateMembersCache(userId, classId, memberGroup);

      const { boardService } = await import('./boardService');
      await boardService.syncNoticeNotesMembersForGroup(userId, classId, memberGroup);
      
      console.log('✅ saveClassMembersBatch - 일괄 저장 완료');
    } catch (error) {
      console.error('클래스 멤버 일괄 저장 실패:', error);
      throw error;
    }
  },

  // 클래스 멤버 일괄 삭제 (파티션 서비스 사용)
  async deleteClassMembersBatch(userId: string, classId: string, memberIds: string[], memberGroup?: string): Promise<void> {
    try {
      console.log('🗑️ deleteClassMembersBatch - 일괄 삭제 시작:', {
        userId, classId, memberIdsCount: memberIds.length, memberGroup
      });
      
      // ClassMembersPartitionService 사용
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      const { boardService } = await import('./boardService');
      
      // 각 멤버 삭제
      for (const memberId of memberIds) {
        await partitionService.deleteMember(memberId);
        await this.__deleteClassMembershipForMember(userId, classId, memberId);
        await boardService.deleteNoticeNotesMemberDoc(userId, classId, memberId);
        await memberProfilePhotoService.deleteMemberPhoto(userId, classId, memberId);
      }
      this.__invalidateMembersCache(userId, classId, memberGroup);
      
      console.log('✅ deleteClassMembersBatch - 일괄 삭제 완료');
    } catch (error) {
      console.error('클래스 멤버 일괄 삭제 실패:', error);
      throw error;
    }
  },

  // 클래스 멤버의 프로필 이미지 업데이트 (파티션 서비스 사용)
  async updateMemberProfileImage(userId: string, classId: string, memberId: string, profileImageId: string, memberGroup?: string): Promise<void> {
    try {
      console.log('🖼️ updateMemberProfileImage - 프로필 이미지 업데이트 시작:', {
        userId, classId, memberId, profileImageId, memberGroup
      });
      
      // ClassMembersPartitionService 사용
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      
      // 프로필 이미지는 기초 정보이므로 모든 파티션에 업데이트
      await partitionService.updateProfileImage(memberId, profileImageId);
      this.__invalidateMembersCache(userId, classId, memberGroup);
      
      console.log('✅ updateMemberProfileImage - 프로필 이미지 업데이트 완료');
    } catch (error) {
      console.error('❌ 프로필 이미지 업데이트 실패:', error);
      throw error;
    }
  },

  async updateMemberPhotoProfile(
    userId: string,
    classId: string,
    memberId: string,
    photoProfilePath?: string,
    photoProfileUrl?: string,
    photoProfileVersion?: number,
    memberGroup?: string
  ): Promise<void> {
    try {
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      await partitionService.updatePhotoProfile(memberId, photoProfilePath, photoProfileUrl, photoProfileVersion);
      this.__invalidateMembersCache(userId, classId, memberGroup);
    } catch (error) {
      console.error('❌ 멤버 사진 프로필 업데이트 실패:', error);
      throw error;
    }
  },

  // 기존 클래스 코드를 usedClassCodes에 등록
  async addClassCodeToExistingClasses(userId: string): Promise<void> {
    try {
      console.log('🔗 기존 클래스 코드 매핑 등록 시작:', { userId });
      
      // 사용자의 모든 클래스 조회
      const classesSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES));
      firebaseReadCounter.increment(classesSnapshot.docs.length);
      
      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data() as Class;
        const classId = classDoc.id;
        
        console.log(`📚 클래스 처리 중: ${classData.name} (${classId})`);
        
        // 이미 클래스 코드가 있는지 확인
        if (classData.classCode) {
          console.log(`  ✅ 클래스 코드 이미 존재: ${classData.classCode}`);
          
          // usedClassCodes에 등록되어 있는지 확인
          try {
            const codeRef = doc(db, COLLECTIONS.USED_CLASS_CODES, classData.classCode);
            const codeDoc = await getDoc(codeRef);
            firebaseReadCounter.increment(1);
            
            if (!codeDoc.exists()) {
              console.log(`  🔗 매핑 등록 중...`);
              await classCodeService.registerClassCode(
                classData.classCode,
                userId,
                classId,
                classData.name
              );
            } else {
              console.log(`  ✅ 매핑 이미 존재`);
            }
          } catch (error) {
            console.error(`  ❌ 매핑 확인/등록 실패:`, error);
          }
        } else {
          console.log(`  ⚠️ 클래스 코드가 없습니다. 새로 생성합니다.`);
          
          // 클래스 코드 생성
          let classCode: string;
          let attempts = 0;
          const maxAttempts = 10;

          // 중복되지 않는 8자리 클래스 코드 생성
          do {
            classCode = classCodeService.generateClassCode();
            const isAvailable = await classCodeService.isClassCodeAvailable(classCode);
            
            if (isAvailable) {
              break;
            }
            
            attempts++;
            if (attempts >= maxAttempts) {
              throw new Error('클래스 코드 생성에 실패했습니다. 다시 시도해주세요.');
            }
          } while (attempts < maxAttempts);

          // 클래스 문서에 클래스 코드 추가
          await setDoc(doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId), {
            ...classData,
            classCode: classCode
          }, { merge: true });
          
          // usedClassCodes에 등록
          await classCodeService.registerClassCode(classCode, userId, classId, classData.name);
          
          console.log(`  ✅ 클래스 코드 생성 및 등록 완료: ${classCode}`);
        }
      }
      
      console.log('✅ 기존 클래스 코드 매핑 등록 완료');
    } catch (error) {
      console.error('❌ 기존 클래스 코드 매핑 등록 실패:', error);
      throw error;
    }
  },

  // === 데이터 마이그레이션 함수 ===
  
  // 클래스 멤버를 배열에서 컬렉션으로 마이그레이션
  async migrateClassMembersToCollection(userId: string, classId: string): Promise<{ success: boolean; migratedCount: number; error?: string }> {
    try {
      console.log('🔄 migrateClassMembersToCollection 시작:', { userId, classId });
      
      // 1. 현재 클래스 데이터 로드 (members 배열 포함)
      const userClasses = await this.loadUserClasses(userId);
      const targetClass = userClasses.find(cls => cls.id === classId);
      
      if (!targetClass) {
        return { success: false, migratedCount: 0, error: '클래스를 찾을 수 없습니다.' };
      }
      
      // 2. members 배열이 있는지 확인
      const members = (targetClass as any).members || [];
      if (members.length === 0) {
        console.log('마이그레이션할 멤버가 없습니다.');
        return { success: true, migratedCount: 0 };
      }
      
      // 3. 멤버들을 새로운 컬렉션 구조로 변환
      const convertedMembers: ClassMember[] = members.map((member: any) => ({
        id: member.id || `member-${member.number}`,
        number: member.number.toString(),
        name: member.name,
        accessCode: member.accessCode,
        notes: '',
        createdAt: new Date(),
        updatedAt: new Date()
      }));
      
      // 4. 새로운 컬렉션에 멤버들 저장
      await this.saveClassMembersBatch(userId, classId, convertedMembers);
      
      // 5. 클래스 문서에서 members 배열 제거 및 memberCount 추가
      const updatedClass = {
        ...targetClass,
        memberCount: convertedMembers.length
      };
      delete (updatedClass as any).members; // members 필드 제거
      
      // 개별 클래스 업데이트
      await this.saveClass(userId, updatedClass);
      
      console.log('✅ migrateClassMembersToCollection 완료:', { migratedCount: convertedMembers.length });
      
      return { success: true, migratedCount: convertedMembers.length };
    } catch (error) {
      console.error('클래스 멤버 마이그레이션 실패:', error);
      return { 
        success: false, 
        migratedCount: 0, 
        error: error instanceof Error ? error.message : '알 수 없는 오류' 
      };
    }
  },

  // 모든 사용자 데이터 조회 (마이그레이션용)
  async getAllUsersData(): Promise<any[]> {
    try {
      console.log('🔍 getAllUsersData - 모든 사용자 데이터 조회 시작');
      
      const usersRef = collection(db, COLLECTIONS.USERS);
      const snapshot = await getDocs(usersRef);
      
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('✅ getAllUsersData - 조회 완료:', { usersCount: usersData.length });
      return usersData;
    } catch (error) {
      console.error('모든 사용자 데이터 조회 실패:', error);
      throw error;
    }
  },

    // 모든 사용자의 클래스 코드 조회 (중복 검사용)
  async getAllClassCodes(): Promise<string[]> {
    try {
      const usersRef = collection(db, COLLECTIONS.USERS);
      const snapshot = await getDocs(usersRef);
      const allCodes: string[] = [];

      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        if (userData.classes && Array.isArray(userData.classes)) {
          userData.classes.forEach((cls: any) => {
            if (cls.classCode && typeof cls.classCode === 'string') {
              allCodes.push(cls.classCode);
            }
          });
        }
      });

      return allCodes;
    } catch (error) {
      console.error('전역 클래스 코드 조회 실패:', error);
      throw error;
    }
  },

  // 사용하지 않는 데이터 정리 (관리자 전용)
  async cleanupUnusedData(adminUserId: string): Promise<{ success: boolean; cleaned: string[]; error?: string }> {
    try {
      // 관리자 권한 확인
      const adminDoc = await getDoc(doc(db, COLLECTIONS.USERS, adminUserId));
      if (!adminDoc.exists() || adminDoc.data()?.userType !== 'admin') {
        return { success: false, cleaned: [], error: '관리자 권한이 필요합니다.' };
      }

      const cleaned: string[] = [];
      const batch = writeBatch(db);

      // 1. 각 사용자의 클래스에 속하지 않은 알람셋 삭제 (users/{userId}/alarmSets)
      const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
      let orphanedAlarmSetsCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        try {
          const userAlarmSetsRef = collection(db, COLLECTIONS.USERS, userDoc.id, 'alarmSets');
          const userAlarmSetsSnapshot = await getDocs(userAlarmSetsRef);
          
          for (const alarmSetDoc of userAlarmSetsSnapshot.docs) {
            batch.delete(alarmSetDoc.ref);
            orphanedAlarmSetsCount++;
          }
        } catch (error) {
          console.warn(`사용자 ${userDoc.id}의 알람셋 삭제 중 오류:`, error);
        }
      }

      if (orphanedAlarmSetsCount > 0) {
        cleaned.push(`클래스에 속하지 않은 알람셋: ${orphanedAlarmSetsCount}개 삭제`);
      }

      // 2. 빈 classes 컬렉션 정리 (users/{userId}/classes에서 알람셋이 없는 항목들)
      let emptyClassCollectionsCount = 0;
      for (const userDoc of usersSnapshot.docs) {
        try {
          const userClassesRef = collection(db, COLLECTIONS.USERS, userDoc.id, COLLECTIONS.CLASSES);
          const userClassesSnapshot = await getDocs(userClassesRef);
          
          // 실제 클래스 데이터와 대조해서 불필요한 컬렉션 문서들 삭제
          const userData = userDoc.data();
          const userClasses = userData.classes || [];
          const validClassIds = userClasses.map((cls: any) => cls.id);

          for (const classDoc of userClassesSnapshot.docs) {
            if (!validClassIds.includes(classDoc.id)) {
              batch.delete(classDoc.ref);
              emptyClassCollectionsCount++;
            }
          }
        } catch (error) {
          console.warn(`사용자 ${userDoc.id}의 클래스 컬렉션 정리 중 오류:`, error);
        }
      }

      if (emptyClassCollectionsCount > 0) {
        cleaned.push(`불필요한 클래스 컬렉션 문서: ${emptyClassCollectionsCount}개 삭제`);
      }

      // 배치 실행
      await batch.commit();

      return { success: true, cleaned };
    } catch (error) {
      console.error('데이터 정리 실패:', error);
      return { success: false, cleaned: [], error: error instanceof Error ? error.message : '알 수 없는 오류' };
    }
  },

  // 데이터베이스 구조 최적화 (관리자 전용)
  async optimizeDatabaseStructure(adminUserId: string): Promise<{ success: boolean; optimized: string[]; error?: string }> {
    try {
      // 관리자 권한 확인
      const adminDoc = await getDoc(doc(db, COLLECTIONS.USERS, adminUserId));
      if (!adminDoc.exists() || adminDoc.data()?.userType !== 'admin') {
        return { success: false, optimized: [], error: '관리자 권한이 필요합니다.' };
      }

      const optimized: string[] = [];
      
      console.log('📊 데이터베이스 구조 최적화 시작...');

      // 현재 구조 분석
      const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
      
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();
        
        console.log(`👤 사용자 ${userId} 분석 중...`);
        
        // 1. 사용자 필드의 클래스 정보 확인
        const userClasses = userData.classes || [];
        
        // 2. 컬렉션으로 저장된 클래스들 확인
        try {
          const userClassesCollectionRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES);
          const userClassesSnapshot = await getDocs(userClassesCollectionRef);
          
          let classCollectionCount = 0;
          let alarmSetsMoved = 0;
          
          for (const classDoc of userClassesSnapshot.docs) {
            const classId = classDoc.id;
            const classCollectionData = classDoc.data();
            
            // 컬렉션에 있는 클래스가 필드에 없다면 추가 (클래스 정보가 컬렉션에만 있는 경우)
            const existsInField = userClasses.find((cls: any) => cls.id === classId);
            
            if (!existsInField && classCollectionData) {
              // 컬렉션의 클래스 정보를 필드로 이동
              const newClass = {
                id: classId,
                name: classCollectionData.name || `클래스 ${classId}`,
                classCode: classCollectionData.classCode,
                members: classCollectionData.members || [],
                createdAt: classCollectionData.createdAt || new Date(),
                isActive: classCollectionData.isActive !== false
              };
              
              userClasses.push(newClass);
              optimized.push(`사용자 ${userId}: 컬렉션의 클래스 ${classId} 정보를 필드로 이동`);
            }
            
            // 3. 클래스 컬렉션 하위의 알람셋들 확인
            const classAlarmSetsRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, COLLECTIONS.ALARM_SETS);
            const classAlarmSetsSnapshot = await getDocs(classAlarmSetsRef);
            
            alarmSetsMoved += classAlarmSetsSnapshot.docs.length;
            classCollectionCount++;
          }
          
          if (classCollectionCount > 0) {
            optimized.push(`사용자 ${userId}: ${classCollectionCount}개 클래스, ${alarmSetsMoved}개 알람셋 구조 확인`);
          }
          
          // 4. 사용자 필드 업데이트 (클래스 정보가 추가된 경우)
          if (userClasses.length > (userData.classes || []).length) {
            await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
              classes: userClasses,
              updatedAt: new Date()
            });
            optimized.push(`사용자 ${userId}: 클래스 필드 정보 업데이트`);
          }
          
        } catch (error) {
          console.warn(`사용자 ${userId} 구조 분석 중 오류:`, error);
        }
      }

      // 5. 전체 구조 요약
      optimized.push(`총 ${usersSnapshot.docs.length}명의 사용자 구조 분석 완료`);
      
      console.log('✅ 데이터베이스 구조 최적화 완료');
      
      return { success: true, optimized };
    } catch (error) {
      console.error('데이터베이스 구조 최적화 실패:', error);
      return { success: false, optimized: [], error: error instanceof Error ? error.message : '알 수 없는 오류' };
    }
  },

  // === 클래스 삭제/복구 관련 함수들 ===
  
  // 클래스와 하위 컬렉션을 deletedClasses로 이동
  async moveClassToDeleted(userId: string, classId: string, deletedClass: DeletedClass): Promise<void> {
    try {
      console.log('🗑️ 클래스를 deletedClasses로 이동 시작:', { userId, classId, className: deletedClass.name });
      
      // 클래스 코드는 보존 (임시 보관함이므로 복구 가능성을 위해)
      console.log('🔒 클래스 코드 보존:', { classCode: deletedClass.classCode });
      
      const batch = writeBatch(db);
      
      // 1. deletedClasses 컬렉션에 완전한 클래스 정보 저장
      const deletedClassRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId);
      
      // undefined 값들을 제거하고 Date 객체를 문자열로 변환
      const cleanDeletedClass = Object.fromEntries(
        Object.entries(deletedClass).filter(([_, value]) => value !== undefined)
      );
      
      batch.set(deletedClassRef, {
        ...cleanDeletedClass,
        createdAt: deletedClass.createdAt instanceof Date ? deletedClass.createdAt.toISOString() : deletedClass.createdAt,
        updatedAt: deletedClass.updatedAt instanceof Date ? deletedClass.updatedAt.toISOString() : new Date().toISOString()
      });
      
      // 2. 기존 classes 컬렉션에서 해당 클래스 문서 삭제
      const classRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId);
      batch.delete(classRef);
      
      // 3. 알람셋 이동 (classes/{classId}/alarmSets → deletedClasses/{classId}/alarmSets)
      const alarmSetsSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, COLLECTIONS.ALARM_SETS));
      alarmSetsSnapshot.docs.forEach(alarmSetDoc => {
        // 기존 알람셋 삭제
        batch.delete(alarmSetDoc.ref);
        // deletedClasses에 알람셋 복사
        const deletedAlarmSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId, COLLECTIONS.ALARM_SETS, alarmSetDoc.id);
        batch.set(deletedAlarmSetRef, alarmSetDoc.data());
      });
      
      // 4. 멤버 이동 (classes/{classId}/members → deletedClasses/{classId}/members)
      const membersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'members'));
      membersSnapshot.docs.forEach(memberDoc => {
        // 기존 멤버 삭제
        batch.delete(memberDoc.ref);
        // deletedClasses에 멤버 복사
        const deletedMemberRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId, 'members', memberDoc.id);
        batch.set(deletedMemberRef, memberDoc.data());
      });
      
      await batch.commit();

      // classMemberships 삭제 (개별 접근 코드로 입장한 멤버 기록)
      await this.__deleteClassMembershipsForClass(userId, classId);
      await memberProfilePhotoService.deleteClassMemberProfileFolder(userId, classId);
      
      console.log('✅ 클래스와 하위 컬렉션 이동 완료:', {
        alarmSetsCount: alarmSetsSnapshot.docs.length,
        membersCount: membersSnapshot.docs.length
      });
    } catch (error) {
      console.error('클래스 이동 실패:', error);
      throw error;
    }
  },
  
  // deletedClasses에서 classes로 복원 (완전한 데이터 복원)
  async restoreClassFromDeleted(userId: string, classId: string): Promise<void> {
    try {
      console.log('♻️ 클래스를 deletedClasses에서 복원 시작:', { userId, classId });
      
      // 먼저 deletedClass에서 완전한 클래스 정보 로드
      const deletedClassRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId);
      const deletedClassSnap = await getDoc(deletedClassRef);
      
      if (!deletedClassSnap.exists()) {
        throw new Error('삭제된 클래스를 찾을 수 없습니다');
      }
      
      const deletedClassData = deletedClassSnap.data() as DeletedClass;
      
      // DeletedClass에서 Class로 변환 (deletedAt, willBeDeletedAt 제거)
      const restoredClass: Class = {
        id: deletedClassData.id,
        name: deletedClassData.name,
        classCode: deletedClassData.classCode,
        classEntryCode: deletedClassData.classEntryCode ?? '0000',
        memberCount: deletedClassData.memberCount,
        createdAt: deletedClassData.createdAt ? new Date(deletedClassData.createdAt) : new Date(),
        isActive: deletedClassData.isActive,
        updatedAt: new Date()
      };
      
      const batch = writeBatch(db);
      
      // 1. classes 컬렉션에 완전한 클래스 정보 복원
      const classRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId);
      
      // undefined 값들을 제거하고 Date 객체를 문자열로 변환
      const cleanRestoredClass = Object.fromEntries(
        Object.entries(restoredClass).filter(([_, value]) => value !== undefined)
      );
      
      batch.set(classRef, {
        ...cleanRestoredClass,
        createdAt: restoredClass.createdAt instanceof Date ? restoredClass.createdAt.toISOString() : restoredClass.createdAt,
        updatedAt: restoredClass.updatedAt instanceof Date ? restoredClass.updatedAt.toISOString() : restoredClass.updatedAt
      });
      
      // 2. 알람셋 복원 (deletedClasses/{classId}/alarmSets → classes/{classId}/alarmSets)
      const deletedAlarmSetsSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId, COLLECTIONS.ALARM_SETS));
      deletedAlarmSetsSnapshot.docs.forEach(alarmSetDoc => {
        // deletedClasses에서 알람셋 삭제
        batch.delete(alarmSetDoc.ref);
        // classes에 알람셋 복원
        const restoredAlarmSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, COLLECTIONS.ALARM_SETS, alarmSetDoc.id);
        batch.set(restoredAlarmSetRef, alarmSetDoc.data());
      });
      
      // 3. 멤버 복원 (deletedClasses/{classId}/members → classes/{classId}/members)
      const deletedMembersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId, 'members'));
      deletedMembersSnapshot.docs.forEach(memberDoc => {
        // deletedClasses에서 멤버 삭제
        batch.delete(memberDoc.ref);
        // classes에 멤버 복원
        const restoredMemberRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'members', memberDoc.id);
        batch.set(restoredMemberRef, memberDoc.data());
      });
      
      // 4. deletedClasses 컬렉션에서 클래스 문서 삭제
      batch.delete(deletedClassRef);
      
      await batch.commit();
      
      // 클래스 코드는 계속 보존되어 있었으므로 재등록 불필요
      console.log('🔒 클래스 코드 계속 사용:', { classCode: restoredClass.classCode });
      
      console.log('✅ 클래스와 하위 컬렉션 복원 완료:', {
        className: restoredClass.name,
        classCode: restoredClass.classCode,
        alarmSetsCount: deletedAlarmSetsSnapshot.docs.length,
        membersCount: deletedMembersSnapshot.docs.length
      });
    } catch (error) {
      console.error('클래스 복원 실패:', error);
      throw error;
    }
  },
  
  // deletedClasses에서 완전 삭제 (즉시 삭제)
  async permanentlyDeleteClass(userId: string, classId: string): Promise<void> {
    try {
      console.log('💀 클래스 완전 삭제 시작:', { userId, classId });
      
      // 0. 삭제될 클래스의 정보 먼저 로드 (클래스 코드 해제용)
      const deletedClassRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId);
      const deletedClassSnapshot = await getDoc(deletedClassRef);
      let classCodeToUnregister = null;
      
      if (deletedClassSnapshot.exists()) {
        const deletedClassData = deletedClassSnapshot.data();
        classCodeToUnregister = deletedClassData.classCode;
        console.log('🔍 삭제될 클래스 정보:', { 
          className: deletedClassData.name,
          classCode: classCodeToUnregister 
        });
      }
      
      const batch = writeBatch(db);
      
      // 1. 알람셋 완전 삭제
      const deletedAlarmSetsSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId, COLLECTIONS.ALARM_SETS));
      deletedAlarmSetsSnapshot.docs.forEach(alarmSetDoc => {
        batch.delete(alarmSetDoc.ref);
      });
      
      // 2. 멤버 완전 삭제
      const deletedMembersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES, classId, 'members'));
      deletedMembersSnapshot.docs.forEach(memberDoc => {
        batch.delete(memberDoc.ref);
      });
      
      // 3. deletedClasses에서 클래스 문서 삭제
      batch.delete(deletedClassRef);
      
      await batch.commit();

      // classMemberships 삭제 (개별 접근 코드로 입장한 멤버 기록)
      await this.__deleteClassMembershipsForClass(userId, classId);
      await memberProfilePhotoService.deleteClassMemberProfileFolder(userId, classId);
      
      // 4. 클래스 코드 해제 (배치 커밋 후 실행)
      if (classCodeToUnregister && classCodeToUnregister !== '000000') {
        await this.unregisterClassCode(classCodeToUnregister);
        console.log('✅ 클래스 코드 해제 완료:', { classCode: classCodeToUnregister });
      }
      
      console.log('✅ 클래스 완전 삭제 완료:', {
        classCode: classCodeToUnregister,
        deletedAlarmSetsCount: deletedAlarmSetsSnapshot.docs.length,
        deletedMembersCount: deletedMembersSnapshot.docs.length
      });
    } catch (error) {
      console.error('클래스 완전 삭제 실패:', error);
      throw error;
    }
  },

  // ===== 클래스 코드 관리 =====
  
  // 클래스 코드가 사용 중인지 확인
  async isClassCodeUsed(code: string): Promise<boolean> {
    try {
      const codeRef = doc(db, COLLECTIONS.USED_CLASS_CODES, code);
      const codeSnapshot = await getDoc(codeRef);
      return codeSnapshot.exists();
    } catch (error) {
      console.error('클래스 코드 중복 검사 실패:', error);
      throw error;
    }
  },

  // 클래스 코드 등록 (클래스 생성/변경 시)
  async registerClassCode(code: string, userId: string, classId: string): Promise<void> {
    try {
      const codeRef = doc(db, COLLECTIONS.USED_CLASS_CODES, code);
      await setDoc(codeRef, {
        userId,
        classId,
        createdAt: new Date()
      });
      console.log('✅ 클래스 코드 등록 완료:', { code, userId, classId });
    } catch (error) {
      console.error('클래스 코드 등록 실패:', error);
      throw error;
    }
  },

  // 클래스 코드 해제 (클래스 삭제 시) - classCodeService의 메서드 사용하여 메타 문서도 함께 업데이트
  async unregisterClassCode(code: string): Promise<void> {
    try {
      if (!code) return; // 코드가 없으면 무시
      
      // classCodeService의 unregisterClassCode를 사용하여 메타 문서도 함께 업데이트
      await classCodeService.unregisterClassCode(code);
      console.log('✅ 클래스 코드 해제 완료:', { code });
    } catch (error) {
      console.error('클래스 코드 해제 실패:', error);
      throw error;
    }
  },

  // 유니크한 클래스 코드 생성
  async generateUniqueClassCode(): Promise<string> {
    try {
      let code: string;
      let attempts = 0;
      const maxAttempts = 10; // 무한 루프 방지
      
      do {
        code = Math.floor(10000000 + Math.random() * 90000000).toString(); // 8자리 랜덤 숫자
        attempts++;
      } while (await this.isClassCodeUsed(code) && attempts < maxAttempts);
      
      if (attempts >= maxAttempts) {
        throw new Error('유니크한 클래스 코드 생성에 실패했습니다.');
      }
      
      console.log('✅ 유니크한 클래스 코드 생성:', { code, attempts });
      return code;
    } catch (error) {
      console.error('유니크한 클래스 코드 생성 실패:', error);
      throw error;
    }
  },

  // 만료된 삭제된 클래스들 정리 (Firestore에서)
  async cleanupExpiredDeletedClasses(userId: string): Promise<void> {
    try {
      console.log('🧹 만료된 삭제 클래스 정리 시작:', { userId });
      
      const deletedClassesSnapshot = await getDocs(collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.DELETED_CLASSES));
      const expiredClasses: { id: string; data: any }[] = [];
      const currentTime = Date.now();
      
      // 만료된 클래스들 찾기
      deletedClassesSnapshot.docs.forEach(doc => {
        const deletedClass = doc.data();
        if (deletedClass.willBeDeletedAt <= currentTime) {
          expiredClasses.push({ id: doc.id, data: deletedClass });
        }
      });
      
      console.log(`🔍 만료된 클래스 ${expiredClasses.length}개 발견`);
      
      // 만료된 클래스들 완전 삭제
      for (const expiredClass of expiredClasses) {
        console.log(`💀 만료된 클래스 자동 삭제: ${expiredClass.data.name} (${expiredClass.id})`);
        await this.permanentlyDeleteClass(userId, expiredClass.id);
      }
      
      console.log('✅ 만료된 삭제 클래스 정리 완료:', { deletedCount: expiredClasses.length });
    } catch (error) {
      console.error('❌ 만료된 삭제 클래스 정리 실패:', error);
      throw error;
    }
  },

  // ===== 과제 설정 관리 (DefaultSet 문서 사용) =====
  
  // 클래스의 과제 설정 저장 (setCollection/DefaultSet 문서에 저장)
  async saveAssignmentSettings(userId: string, classId: string, settings: AssignmentSettings): Promise<void> {
    try {
      console.log('💾 과제 설정 저장 시작 (DefaultSet):', { userId, classId, settings });
      await defaultSetService.saveAssignmentSettings(userId, classId, settings);
      console.log('✅ 과제 설정 저장 완료 (DefaultSet):', { userId, classId });
    } catch (error) {
      console.error('❌ 과제 설정 저장 실패:', error);
      throw error;
    }
  },

  // 클래스의 과제 설정 로드 (setCollection/DefaultSet 문서에서 로드)
  async loadAssignmentSettings(userId: string, classId: string): Promise<AssignmentSettings | null> {
    try {
      console.log('🔍 과제 설정 로드 시작 (DefaultSet):', { userId, classId });
      const settings = await defaultSetService.loadAssignmentSettings(userId, classId);
      
      if (settings) {
        console.log('✅ 과제 설정 로드 완료 (DefaultSet):', { userId, classId, settings });
        return settings;
      }
      
      console.log('⚠️ 과제 설정이 존재하지 않음 (DefaultSet):', { userId, classId });
      return null;
    } catch (error) {
      console.error('❌ 과제 설정 로드 실패:', error);
      throw error;
    }
  },

  // 클래스의 과제 설정 초기화 (기본값으로) - DefaultSet 초기화
  async initializeAssignmentSettings(userId: string, classId: string): Promise<void> {
    try {
      console.log('🔧 과제 설정 초기화 시작 (DefaultSet):', { userId, classId });
      await defaultSetService.initializeDefaultSet(userId, classId);
      console.log('✅ 과제 설정 초기화 완료 (DefaultSet):', { userId, classId });
    } catch (error) {
      console.error('❌ 과제 설정 초기화 실패:', error);
      throw error;
    }
  },

  // === 멤버 삭제 시 연관 데이터 정리 ===
  
  /**
   * 멤버 삭제 시 모든 연관 과제 데이터 정리 (트랜잭션 보장)
   */
  async deleteStudentsWithAssignmentCleanup(
    userId: string,
    classId: string,
    studentIds: string[],
    memberGroup?: string
  ): Promise<void> {
    try {
      // 과제 관련 데이터 정리 (각 서비스별로 병렬 처리)
      // 동적 임포트를 사용하여 순환 의존성 회피
      const [
        { assignmentService },
        { projectAssignmentService },
        { routineAssignmentService }
      ] = await Promise.all([
        import('./assignmentService'),
        import('./projectAssignmentService'),
        import('./routineAssignmentService')
      ]);

      // 모든 과제 타입의 데이터를 병렬로 정리
      await Promise.all([
        assignmentService.cleanupStudentAssignmentData(userId, classId, studentIds),
        projectAssignmentService.cleanupStudentProjectAssignmentData(userId, classId, studentIds),
        routineAssignmentService.cleanupStudentRoutineAssignmentData(userId, classId, studentIds)
      ]);

      // 마지막으로 멤버 멤버 데이터 삭제
      await this.deleteClassMembersBatch(userId, classId, studentIds, memberGroup);
    } catch (error) {
      console.error('멤버 삭제 및 연관 데이터 정리 실패:', error);
      throw new Error(`멤버 삭제 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  },

  /**
   * 멤버 그룹 삭제 시 그룹 전용 컬렉션/문서 정리
   */
  async deleteMemberGroupData(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<void> {
    const g = (memberGroup || 'A').toString().trim().toUpperCase() || 'A';
    const basePath = `users/${userId}/classes/${classId}`;
    const groupSuffix = g === 'A' ? '' : `_${g}`;

    const deleteCollectionByPath = async (collectionPath: string): Promise<number> => {
      const snapshot = await getDocs(collection(db, collectionPath));
      if (snapshot.empty) return 0;

      const batchSize = 450;
      let currentBatch = writeBatch(db);
      let batchCount = 0;
      let deleted = 0;

      for (const docSnapshot of snapshot.docs) {
        currentBatch.delete(docSnapshot.ref);
        batchCount++;
        deleted++;

        if (batchCount >= batchSize) {
          await currentBatch.commit();
          currentBatch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await currentBatch.commit();
      }

      return deleted;
    };

    const deleteRoutineAssignmentSubcollections = async (collectionPath: string): Promise<void> => {
      const routineSnap = await getDocs(collection(db, collectionPath));
      for (const routineDoc of routineSnap.docs) {
        const datesPath = `${collectionPath}/${routineDoc.id}/dates`;
        const legacyDatePath = `${collectionPath}/${routineDoc.id}/DateAssignment`;
        await deleteCollectionByPath(datesPath);
        await deleteCollectionByPath(legacyDatePath);
      }
    };

    try {
      // 1) 멤버 관련 컬렉션 (그룹 전용)
      const memberCollections = [
        `ClassMembersProfile${groupSuffix}`,
        `ClassMembersNote${groupSuffix}`,
        `ClassMembersPoint${groupSuffix}`,
        `ClassMembersCoin${groupSuffix}`
      ];

      for (const collectionName of memberCollections) {
        await deleteCollectionByPath(`${basePath}/${collectionName}`);
      }

      // 2) 과제 상태 컬렉션 (그룹 전용)
      const generalStatuses = withMemberGroup('generalAssignmentStatuses', g);
      const projectStatuses = withMemberGroup('projectAssignmentStatuses', g);
      const routineStatuses = withMemberGroup('routineAssignmentStatuses', g);

      await deleteCollectionByPath(`${basePath}/${generalStatuses}`);
      await deleteCollectionByPath(`${basePath}/${projectStatuses}`);

      // 루틴 과제는 하위 컬렉션까지 정리 후 루트 삭제
      const routinePath = `${basePath}/${routineStatuses}`;
      await deleteRoutineAssignmentSubcollections(routinePath);
      await deleteCollectionByPath(routinePath);

      // 3) 게시판 공지(NoticeNote) 그룹별 문서 삭제 (양식: NoticeNote_B-1)
      const noticeNotesPath = `${basePath}/ClassBoard/NoticeBook/NoticeNotes`;
      const prefix = g === 'A' ? 'NoticeNote-' : `NoticeNote_${g}-`;
      const noticeNotesRef = collection(db, noticeNotesPath);
      const q = query(
        noticeNotesRef,
        orderBy(documentId()),
        startAt(prefix),
        endAt(prefix + '\uf8ff')
      );
      const notesSnap = await getDocs(q);

      if (!notesSnap.empty) {
        const batchSize = 450;
        let currentBatch = writeBatch(db);
        let batchCount = 0;
        for (const docSnapshot of notesSnap.docs) {
          currentBatch.delete(docSnapshot.ref);
          batchCount++;
          if (batchCount >= batchSize) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            batchCount = 0;
          }
        }
        if (batchCount > 0) {
          await currentBatch.commit();
        }
      }

      // 삭제된 그룹의 멤버 캐시 무효화 (B 그룹 재생성 시 캐시된 구 멤버 목록이 나오지 않도록)
      this.__invalidateMembersCache(userId, classId, memberGroup);
    } catch (error) {
      console.error('그룹 데이터 삭제 실패:', { userId, classId, memberGroup, error });
      throw error;
    }
  },

  // === 레벨 설정 관리 ===
  
        /**
         * 레벨 설정 저장
         */
        async saveLevelSettings(
          userId: string,
          classId: string,
          settings: { 
            levelUpPoints: number; 
            selectedLevel: number;
            levelCoins?: { [level: number]: number };
            levelItemCards?: { [level: number]: string[] };
          }
        ): Promise<void> {
          try {
            console.log('💾 레벨 설정 저장 시작:', { userId, classId, settings });

            // 올바른 경로에 저장: users/{userId}/classes/{classId}/setCollection/pointLevelSet
            const pointLevelSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'setCollection', 'pointLevelSet');

            // 기존 PointLevelSet 문서를 먼저 읽어서 기존 데이터 유지
            const existingDoc = await getDoc(pointLevelSetRef);
            let existingData = {};
            
            if (existingDoc.exists()) {
              existingData = existingDoc.data();
              console.log('🔍 기존 PointLevelSet 문서 데이터:', existingData);
            }
            
            const dataToSave = {
              ...existingData, // 기존 데이터 유지
              levelUpPoints: settings.levelUpPoints,
              selectedLevel: settings.selectedLevel,
              levelCoins: settings.levelCoins || {},
              levelItemCards: settings.levelItemCards || {},
              updatedAt: new Date()
            };
            
            console.log('🔍 저장할 데이터:', dataToSave);
            console.log('🔍 levelCoins 저장 데이터:', settings.levelCoins);
            console.log('🔍 levelItemCards 저장 데이터:', settings.levelItemCards);
            
            await setDoc(pointLevelSetRef, dataToSave);

            console.log('✅ 레벨 설정 저장 완료:', { userId, classId });
          } catch (error) {
            console.error('❌ 레벨 설정 저장 실패:', error);
            throw error;
          }
        },

        /**
         * 레벨 설정 로드
         */
        async loadLevelSettings(userId: string, classId: string): Promise<{ 
          levelUpPoints: number; 
          selectedLevel: number;
          levelCoins?: { [level: number]: number };
          levelItemCards?: { [level: number]: string[] };
        } | null> {
          try {
            console.log('📖 레벨 설정 로드 시작:', { userId, classId });

            // 1. 먼저 올바른 경로에서 로드 시도: users/{userId}/classes/{classId}/setCollection/pointLevelSet
            const pointLevelSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'setCollection', 'pointLevelSet');
            console.log('🔍 새로운 경로에서 로드 시도:', `users/${userId}/classes/${classId}/setCollection/pointLevelSet`);
            const settingsSnap = await getDoc(pointLevelSetRef);

            if (settingsSnap.exists()) {
              const data = settingsSnap.data();
              console.log('✅ ClassMembersPoint에서 레벨 설정 로드 완료:', data);
              console.log('🔍 levelCoins 데이터:', data.levelCoins);
              console.log('🔍 levelItemCards 데이터:', data.levelItemCards);
              return {
                levelUpPoints: data.levelUpPoints || 50,
                selectedLevel: data.selectedLevel || 1,
                levelCoins: data.levelCoins || {},
                levelItemCards: data.levelItemCards || {}
              };
            } else {
              console.log('❌ 새로운 경로에 데이터 없음');
            }

            // 2. ClassMembersPoint에 데이터가 없으면 기존 경로에서 로드 시도
            console.log('📖 ClassMembersPoint에 데이터 없음 - 기존 경로에서 로드 시도');
            const oldSettingsRef = doc(db, COLLECTIONS.USERS, userId, 'classes', classId, 'settings', 'level');
            console.log('🔍 기존 경로에서 로드 시도:', `users/${userId}/classes/${classId}/settings/level`);
            const oldSettingsSnap = await getDoc(oldSettingsRef);

            if (oldSettingsSnap.exists()) {
              const data = oldSettingsSnap.data();
              console.log('✅ 기존 경로에서 레벨 설정 로드 완료:', data);
              console.log('🔍 levelCoins 데이터:', data.levelCoins);
              console.log('🔍 levelItemCards 데이터:', data.levelItemCards);
              
              // 기존 데이터를 새로운 경로로 마이그레이션
              console.log('🔄 기존 데이터를 새로운 경로로 마이그레이션 시작');
              await this.saveLevelSettings(userId, classId, {
                levelUpPoints: data.levelUpPoints || 50,
                selectedLevel: data.selectedLevel || 1,
                levelCoins: data.levelCoins || {},
                levelItemCards: data.levelItemCards || {}
              });
              console.log('✅ 마이그레이션 완료');
              
              return {
                levelUpPoints: data.levelUpPoints || 50,
                selectedLevel: data.selectedLevel || 1,
                levelCoins: data.levelCoins || {},
                levelItemCards: data.levelItemCards || {}
              };
            } else {
              console.log('❌ 기존 경로에도 데이터 없음');
            }

            // 3. 둘 다 없으면 기본값 반환
            console.log('📖 레벨 설정 없음 - 기본값 반환');
            console.log('🔍 기본값으로 반환되는 데이터:', {
              levelUpPoints: 50,
              selectedLevel: 1,
              levelCoins: {},
              levelItemCards: {}
            });
            return {
              levelUpPoints: 50,
              selectedLevel: 1,
              levelCoins: {},
              levelItemCards: {}
            };
          } catch (error) {
            console.error('❌ 레벨 설정 로드 실패:', error);
            throw error;
          }
        },

  // 클래스 초기화 (멤버 정보만 남기고 나머지 모두 삭제)
  async resetClass(userId: string, classId: string): Promise<void> {
    try {
      console.log('🔄 클래스 초기화 시작:', { userId, classId });
      
      const deleteCounts: Record<string, number> = {};

      // 헬퍼 함수: 컬렉션의 모든 문서 삭제 (batch 제한 고려)
      const deleteCollection = async (collectionRef: ReturnType<typeof collection>): Promise<number> => {
        const snapshot = await getDocs(collectionRef);
        let count = 0;
        
        // 450개씩 나누어 batch 처리 (500개 제한 고려)
        const batchSize = 450;
        let currentBatch = writeBatch(db);
        let batchCount = 0;
        
        for (const docSnapshot of snapshot.docs) {
          currentBatch.delete(docSnapshot.ref);
          batchCount++;
          count++;
          
          if (batchCount >= batchSize) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            batchCount = 0;
          }
        }
        
        // 남은 문서 처리
        if (batchCount > 0) {
          await currentBatch.commit();
        }
        
        return count;
      };

      // 삭제할 컬렉션 목록
      const collectionsToDelete = [
        'ClassChat',
        'ClassChatQuizzes',
        'ClassChatQuizSets',
        'ClassMembersPoint',
        'ClassMembersCoin',  // 코인 데이터 컬렉션 추가
        'ClassMembersNote',
        'alarmSets',
        'assignmentCategories',
        'generalAssignmentStatuses',
        'projectAssignmentStatuses',
        'generalAssignments',
        'projectAssignments',
        'routineAssignments',
        'members',
        'membersPoints',
        'studentPoints'
      ];
      
      // 각 컬렉션 삭제
      for (const collectionName of collectionsToDelete) {
        console.log(`📋 ${collectionName} 삭제 중...`);
        const collectionRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, collectionName);
        deleteCounts[collectionName] = await deleteCollection(collectionRef);
        console.log(`✅ ${collectionName} 삭제 완료: ${deleteCounts[collectionName]}개`);
      }
      
      // ClassBoard/NoteBook/notes 파티션 문서의 notes 배열 초기화
      console.log('📋 ClassBoard/NoteBook/notes 파티션 초기화 중...');
      try {
        const noteBookRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'ClassBoard', 'NoteBook');
        const notesRef = collection(noteBookRef, 'notes');
        const notesSnapshot = await getDocs(notesRef);
        
        let notesResetCount = 0;
        for (const notePartitionDoc of notesSnapshot.docs) {
          // 각 파티션 문서 (note-1 등)의 notes 배열 초기화
          await updateDoc(notePartitionDoc.ref, {
            notes: []
          });
          notesResetCount++;
        }
        deleteCounts['ClassBoard/NoteBook/notes'] = notesResetCount;
        console.log(`✅ ClassBoard/NoteBook/notes 파티션 초기화 완료: ${notesResetCount}개`);
      } catch (error) {
        console.log('⚠️ ClassBoard/NoteBook/notes 초기화 중 오류 (없을 수 있음):', error);
      }
      
      // setCollection/pointLevelSet 문서 삭제
      console.log('📋 setCollection/pointLevelSet 삭제 중...');
      try {
        const pointLevelSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'setCollection', 'pointLevelSet');
        const pointLevelSetDoc = await getDoc(pointLevelSetRef);
        
        if (pointLevelSetDoc.exists()) {
          await deleteDoc(pointLevelSetRef);
          deleteCounts['setCollection/pointLevelSet'] = 1;
          console.log(`✅ setCollection/pointLevelSet 삭제 완료`);
        }
      } catch (error) {
        console.log('⚠️ setCollection/pointLevelSet 삭제 중 오류 (없을 수 있음):', error);
      }
      
      // setCollection/generalSet 문서 삭제
      console.log('📋 setCollection/generalSet 삭제 중...');
      try {
        const generalSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'setCollection', 'generalSet');
        const generalSetDoc = await getDoc(generalSetRef);
        
        if (generalSetDoc.exists()) {
          await deleteDoc(generalSetRef);
          deleteCounts['setCollection/generalSet'] = 1;
          console.log(`✅ setCollection/generalSet 삭제 완료`);
        }
      } catch (error) {
        console.log('⚠️ setCollection/generalSet 삭제 중 오류 (없을 수 있음):', error);
      }
      
      // setCollection/itemCardSet 문서 삭제
      console.log('📋 setCollection/itemCardSet 삭제 중...');
      try {
        const itemCardSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'setCollection', 'itemCardSet');
        const itemCardSetDoc = await getDoc(itemCardSetRef);
        
        if (itemCardSetDoc.exists()) {
          await deleteDoc(itemCardSetRef);
          deleteCounts['setCollection/itemCardSet'] = 1;
          console.log(`✅ setCollection/itemCardSet 삭제 완료`);
        }
      } catch (error) {
        console.log('⚠️ setCollection/itemCardSet 삭제 중 오류 (없을 수 있음):', error);
      }
      
      // setCollection/DefaultSet 문서 삭제
      console.log('📋 setCollection/DefaultSet 삭제 중...');
      try {
        const defaultSetRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'setCollection', 'DefaultSet');
        const defaultSetDoc = await getDoc(defaultSetRef);
        
        if (defaultSetDoc.exists()) {
          await deleteDoc(defaultSetRef);
          deleteCounts['setCollection/DefaultSet'] = 1;
          console.log(`✅ setCollection/DefaultSet 삭제 완료`);
        }
      } catch (error) {
        console.log('⚠️ setCollection/DefaultSet 삭제 중 오류 (없을 수 있음):', error);
      }
      
      // routineAssignmentStatuses와 하위 DateAssignment 서브컬렉션 삭제
      console.log('📋 routineAssignmentStatuses 삭제 중...');
      const routineStatusesRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'routineAssignmentStatuses');
      const routineStatusesSnapshot = await getDocs(routineStatusesRef);
      let routineDeleteCount = 0;
      for (const routineDoc of routineStatusesSnapshot.docs) {
        // DateAssignment 서브컬렉션 삭제
        const dateAssignmentRef = collection(routineDoc.ref, 'DateAssignment');
        routineDeleteCount += await deleteCollection(dateAssignmentRef);
        // 루틴 문서 삭제
        await deleteDoc(routineDoc.ref);
        routineDeleteCount++;
      }
      deleteCounts['routineAssignmentStatuses'] = routineDeleteCount;
      console.log(`✅ routineAssignmentStatuses 삭제 완료: ${routineDeleteCount}개`);
      
      // ClassMembers 파티션 문서에서 멤버 필드 초기화
      console.log('📋 ClassMembers 멤버 필드 초기화 중...');
      const classMembersRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId, 'ClassMembers');
      const classMembersSnapshot = await getDocs(classMembersRef);
      
      let membersUpdated = 0;
      for (const partitionDoc of classMembersSnapshot.docs) {
        const partitionData = partitionDoc.data();
        
        if (partitionData.members && Array.isArray(partitionData.members)) {
          // 각 멤버의 초기화 대상 필드를 초기화
          const updatedMembers = partitionData.members.map((member: Record<string, unknown>) => {
            // coins, coinsHistory, itemCards, profileImage, unlockedProfiles 제거
            const { coins, coinsHistory, itemCards, profileImage, unlockedProfiles, ...rest } = member;
            
            // 기본 정보만 유지
            const cleanedMember = {
              ...rest,
              // 초기화 대상 필드들
              pendingNotifications: [],
              seatPositions: null
            };
            return cleanedMember;
          });
          
          // 파티션 문서 업데이트
          await updateDoc(partitionDoc.ref, {
            members: updatedMembers
          });
          
          membersUpdated += updatedMembers.length;
          console.log(`✅ ${partitionDoc.id} 파티션 ${updatedMembers.length}명 멤버 필드 초기화 완료 (coins, coinsHistory, itemCards, profileImage, unlockedProfiles 제거)`);
        }
      }
      
      deleteCounts['membersFieldsReset'] = membersUpdated;
      
      // 클래스 문서의 설정값 초기화 (멤버 정보는 유지)
      console.log('📋 클래스 설정 초기화 중...');
      const classRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CLASSES, classId);
      const classDocSnapshot = await getDoc(classRef);
      
      if (classDocSnapshot.exists()) {
        const classData = classDocSnapshot.data();
        await updateDoc(classRef, {
          // 클래스 코드는 유지
          classCode: classData.classCode || '000000',
          // 생성일은 유지
          createdAt: classData.createdAt,
          // 업데이트일은 현재로
          updatedAt: new Date()
        });
      }
      
      console.log('✅ 클래스 초기화 완료:', deleteCounts);
    } catch (error) {
      console.error('❌ 클래스 초기화 실패:', error);
      throw error;
    }
  }
};